import { connect, ConnectedProps } from 'react-redux';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Team } from 'dashboard/models/team';
import { showMessage } from 'dashboard/redux/actions/messages';
import { setTeam } from 'dashboard/redux/actions/teams';
import { RootState } from 'dashboard/redux/store';
import { loadTeams } from 'dashboard/redux/thunk-actions/teams';
import DashboardTeamsView from 'dashboard/views/DashboardTeamsView';
import { CreateTeamFormData } from 'dashboard/views/DashboardTeamsView/DashboardTeamsView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardTeamsRoute = (props: PropsFromRedux) => {
  const { loading: teamsLoading } = useApiLoader({
    loader: () => props.loadTeams(),
    deps: [props.loadTeams],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load teams.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: createTeamError, callback: createTeam } = useApiCallback(
    async ({ name }: CreateTeamFormData) => {
      const teamsResponse = await SessionClientDashboard.postResourceCreate<Team>({
        attributes: {
          name,
        },
        type: ApiModel.Team,
      });

      props.setTeam(teamsResponse.data);
      props.showMessage('Team was successfully created.', MessageType.Success);
    },
    [props.showMessage, props.setTeam],
  );

  useApiErrorHandler({
    error: createTeamError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to create a new team, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: deleteTeamError, callback: deleteTeam } = useApiCallback(
    async (teamId: string) => {
      const deleteTeamResponse = await SessionClientDashboard.deleteResource<Team>(
        ApiEndpoint.Team,
        teamId,
      );
      props.setTeam(deleteTeamResponse.data);
      props.showMessage('Successfully deleted team.', MessageType.Success);
    },
    [props.showMessage, props.setTeam],
  );

  useApiErrorHandler({
    error: deleteTeamError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to deleted team, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to deleted team, please try again.', MessageType.Failure),
  });

  return (
    <DashboardTeamsView
      teams={props.teams}
      teamsLoading={teamsLoading}
      createTeam={createTeam}
      deleteTeam={deleteTeam}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    teams: state.teams.teams,
  };
};

const mapDispatchToProps = {
  loadTeams,
  setTeam,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardTeamsRoute);
